<template>
    <v-card class="app-screen-bg pl-2 pl-md-10 trade-hub-page">
        <div class="trade-hub-page__scroll">
            <div class="trade-hub-title-pill-wrap">
                <span class="trade-hub-title-pill">{{ $t('trade.hub.title') }}</span>
            </div>
            <div class="trade-hub-city-notice" :class="{'error--text': !isInCity}">
                <v-icon size="14" :color="isInCity ? undefined : 'error'">mdi-city-variant-outline</v-icon>
                {{ $t('trade.hub.mustBeInCity') }}
            </div>

            <div v-if="activeTab === 'marketplace'" class="trade-hub-hint">
                {{ $t('trade.hub.strongerAlwaysHint') }}
            </div>

            <div class="trade-hub-tabs">
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    type="button"
                    class="trade-hub-tab-pill"
                    :class="{'trade-hub-tab-pill--active': activeTab === tab.id}"
                    @click="activeTab = tab.id"
                >{{ tab.label }}</button>
            </div>

            <template v-if="activeTab === 'marketplace'">
                <div class="trade-hub-section" v-if="myOpenOffers.length > 0">
                    <div class="trade-hub-section__title">{{ $t('trade.hub.myOffersTitle') }}</div>
                    <div
                        v-for="offer in myOpenOffers"
                        :key="'mine-' + offer.id"
                        class="trade-hub-row trade-hub-row--static"
                        :class="transparentClass"
                    >
                        <v-img v-if="offer.offeredItem" :src="require(`@/assets/pic${offer.offeredItem.picture}`)" class="trade-hub-row__stone-img" contain/>
                        <div class="trade-hub-row__body">
                            <div class="trade-hub-row__name">{{ offer.offeredItem?.name }} ({{ offer.requiredPowerRank }})</div>
                            <div class="trade-hub-row__preview">{{ $t('trade.hub.wantsLabel') }}: {{ wantedTypeNames(offer) }}</div>
                            <div v-if="offer.acceptWeakerStone" class="trade-hub-row__weaker-badge">{{ $t('trade.hub.acceptsWeakerBadge') }}</div>
                            <div class="trade-hub-row__mine-badge">{{ $t('trade.hub.myOfferBadge') }}</div>
                        </div>
                        <v-btn icon x-small @click.stop="cancelOffer(offer)">
                            <v-icon size="18">mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>

                <div class="trade-hub-section">
                    <div class="trade-hub-section__title">{{ $t('trade.hub.otherOffersTitle') }}</div>

                    <div class="trade-hub-filters">
                        <button type="button" class="trade-hub-filters-toggle" @click="filtersOpen = !filtersOpen">
                            {{ $t('trade.hub.filtersToggleButton') }}
                            <span v-if="activeFilterCount > 0" class="trade-hub-filters-badge">{{ activeFilterCount }}</span>
                        </button>
                        <div v-if="filtersOpen" class="trade-hub-filters-panel">
                            <div class="trade-hub-filters-group-label">{{ $t('trade.hub.filtersOfferedTypeGroupLabel') }}</div>
                            <div class="trade-hub-type-chips mb-2">
                                <v-chip
                                    v-for="type in availableOfferedTypesForFilter"
                                    :key="'offered-type-' + type.id"
                                    :style="typeChipStyle(type.id)"
                                    :class="{'trade-hub-type-chip--active': filterOfferedTypeIds.includes(type.id)}"
                                    small
                                    class="ma-1"
                                    @click="toggleInFilterList(filterOfferedTypeIds, type.id)"
                                >{{ $t('common.typeNames.' + type.name) }}</v-chip>
                            </div>

                            <div class="trade-hub-filters-group-label">{{ $t('trade.hub.filtersOfferedPowerGroupLabel') }}</div>
                            <div class="trade-hub-type-chips mb-2">
                                <v-chip
                                    v-for="rank in availableOfferedRanksForFilter"
                                    :key="'offered-rank-' + rank"
                                    :class="{'trade-hub-type-chip--active': filterOfferedRanks.includes(rank)}"
                                    small
                                    class="ma-1"
                                    @click="toggleInFilterList(filterOfferedRanks, rank)"
                                >{{ rank }}</v-chip>
                            </div>

                            <div class="trade-hub-filters-group-label">{{ $t('trade.hub.filtersWantedTypeGroupLabel') }}</div>
                            <div class="trade-hub-type-chips mb-2">
                                <v-chip
                                    v-for="type in availableWantedTypesForFilter"
                                    :key="'wanted-type-' + type.id"
                                    :style="typeChipStyle(type.id)"
                                    :class="{'trade-hub-type-chip--active': filterWantedTypeIds.includes(type.id)}"
                                    small
                                    class="ma-1"
                                    @click="toggleInFilterList(filterWantedTypeIds, type.id)"
                                >{{ $t('common.typeNames.' + type.name) }}</v-chip>
                            </div>

                            <v-btn v-if="activeFilterCount > 0" text x-small color="primary" @click="clearFilters">
                                {{ $t('trade.hub.filtersClearButton') }}
                            </v-btn>
                        </div>
                    </div>

                    <div
                        v-for="offer in filteredMarketplaceOffers"
                        :key="offer.id"
                        class="trade-hub-row"
                        :class="[transparentClass, {'trade-hub-row--unavailable': !canFulfill(offer)}]"
                        @click="openFulfillDialog(offer)"
                    >
                        <v-img v-if="offer.offeredItem" :src="require(`@/assets/pic${offer.offeredItem.picture}`)" class="trade-hub-row__stone-img" contain/>
                        <div class="trade-hub-row__body">
                            <div class="trade-hub-row__name">{{ offer.offeredItem?.name }} ({{ offer.requiredPowerRank }})</div>
                            <div class="trade-hub-row__preview">{{ $t('trade.hub.wantsLabel') }}: {{ wantedTypeNames(offer) }}</div>
                            <div v-if="offer.acceptWeakerStone" class="trade-hub-row__weaker-badge">{{ $t('trade.hub.acceptsWeakerBadge') }}</div>
                            <div class="trade-hub-row__seller">{{ $t('trade.hub.sellerLabel') }}: {{ offer.seller?.name }}</div>
                        </div>
                    </div>
                    <div v-if="!marketplaceLoading && marketplaceOffers.length === 0" class="trade-hub-empty text-center grey--text pa-4">
                        {{ $t('trade.hub.noOffers') }}
                    </div>
                    <div v-if="!marketplaceLoading && marketplaceOffers.length > 0 && filteredMarketplaceOffers.length === 0" class="trade-hub-empty text-center grey--text pa-4">
                        {{ $t('trade.hub.noOffersMatchFilters') }}
                    </div>
                </div>
            </template>

            <template v-else>
                <div class="trade-hub-section">
                    <div class="trade-hub-section__title">{{ $t('trade.hub.createTitle') }}</div>
                    <v-select
                        v-model="newOfferItemId"
                        :items="myUnlockedStoneOptions"
                        item-text="label"
                        item-value="id"
                        :label="$t('trade.hub.selectStoneLabel')"
                        outlined
                        dense
                        hide-details
                        class="mb-3"
                    />
                    <div class="trade-hub-type-chips mb-3">
                        <v-chip
                            v-for="type in availableWantedTypesForNewOffer"
                            :key="type.id"
                            :style="typeChipStyle(type.id)"
                            :class="{'trade-hub-type-chip--active': newOfferWantedTypeIds.includes(type.id)}"
                            small
                            class="ma-1"
                            @click="toggleWantedType(type.id)"
                        >{{ $t('common.typeNames.' + type.name) }}</v-chip>
                    </div>
                    <v-checkbox
                        v-model="newOfferAcceptWeakerStone"
                        :disabled="isNewOfferStoneWeakest"
                        :label="$t('trade.hub.createAcceptWeakerStoneLabel')"
                        dense
                        hide-details
                        class="mb-2"
                    />
                    <div v-if="isNewOfferStoneWeakest" class="trade-hub-weakest-hint mb-2">
                        {{ $t('trade.hub.createAcceptWeakerStoneWeakestHint') }}
                    </div>
                    <v-alert v-if="createError" type="error" dense class="text-left">{{ createError }}</v-alert>
                    <GateHint
                        v-if="!isInCity"
                        :satisfied="false"
                        icon="mdi-city-variant-outline"
                        :message="$t('trade.hub.mustBeInCity')"
                        class="mb-2"
                    />
                    <v-btn
                        color="primary black--text btn-shadow"
                        small
                        :loading="createBusy"
                        :disabled="!newOfferItemId || newOfferWantedTypeIds.length === 0 || !isInCity"
                        @click="submitCreateOffer"
                    >{{ $t('trade.hub.createButton') }}</v-btn>
                </div>

                <div class="trade-hub-section">
                    <div class="trade-hub-section__title">{{ $t('trade.hub.myOffersTitle') }}</div>
                    <div
                        v-for="offer in myOffers"
                        :key="offer.id"
                        class="trade-hub-row"
                        :class="transparentClass"
                    >
                        <v-img v-if="offer.offeredItem" :src="require(`@/assets/pic${offer.offeredItem.picture}`)" class="trade-hub-row__stone-img" contain/>
                        <div class="trade-hub-row__body">
                            <div class="trade-hub-row__name">{{ offer.offeredItem?.name }} ({{ offer.requiredPowerRank }})</div>
                            <div class="trade-hub-row__preview">{{ statusLabel(offer) }}</div>
                        </div>
                        <v-btn v-if="offer.status === 'OPEN'" icon x-small @click.stop="cancelOffer(offer)">
                            <v-icon size="18">mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <div v-if="myOffers.length === 0" class="trade-hub-empty text-center grey--text pa-4">
                        {{ $t('trade.hub.noMyOffers') }}
                    </div>
                </div>
            </template>
        </div>

        <ResponsiveDialog v-model="fulfillDialogOpen" :max-width="420" :full-height="false" :header-title="$t('trade.hub.fulfillDialogTitle')">
            <v-card-text v-if="fulfillOfferTarget" class="text-center pa-4 trade-hub-fulfill-body">
                <p>{{ $t('trade.hub.fulfillPromptPrefix') }} <strong>{{ fulfillOfferTarget.offeredItem?.name }}</strong> ({{ fulfillOfferTarget.requiredPowerRank }})</p>
                <v-select
                    v-model="fulfillMyItemId"
                    :items="matchingStoneOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('trade.hub.selectYourStoneLabel')"
                    outlined
                    dense
                    hide-details
                    class="mb-4"
                />
                <v-alert v-if="matchingStoneOptions.length === 0" type="info" dense class="text-left">{{ $t('trade.hub.noMatchingStones') }}</v-alert>
                <v-alert v-if="fulfillError" type="error" dense class="text-left">{{ fulfillError }}</v-alert>
                <GateHint
                    v-if="!isInCity"
                    :satisfied="false"
                    icon="mdi-city-variant-outline"
                    :message="$t('trade.hub.mustBeInCity')"
                    class="mb-2 justify-center"
                />
                <v-btn
                    color="primary black--text btn-shadow"
                    block
                    :loading="fulfillBusy"
                    :disabled="!fulfillMyItemId || !isInCity"
                    @click="submitFulfill"
                >{{ $t('trade.hub.fulfillButton') }}</v-btn>
            </v-card-text>
        </ResponsiveDialog>

        <ConfirmDialog ref="confirmDialog"/>
    </v-card>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
import ConfirmDialog from '@/components/utils/ConfirmDialog';
import GateHint from '@/components/utils/GateHint';
import {Categories, StonePowerRankOrder} from '@/utils/itemsEnum';
import {API_ENTRYPOINT} from '@/config/entrypoint';
import {typeChipStyle} from '@/utils/typeColorEnum';
import {Positions} from '@/utils/positionEnum';

export default {
    name: 'TradeHub',
    components: {ResponsiveDialog, ConfirmDialog, GateHint},
    data() {
        return {
            activeTab: 'marketplace',
            marketplaceOffers: [],
            marketplaceLoading: false,
            myOffers: [],
            myStones: [],
            allTypes: [],
            newOfferItemId: null,
            newOfferWantedTypeIds: [],
            newOfferAcceptWeakerStone: false,
            createBusy: false,
            createError: '',
            fulfillDialogOpen: false,
            fulfillOfferTarget: null,
            fulfillMyItemId: null,
            fulfillBusy: false,
            fulfillError: '',
            filtersOpen: false,
            filterOfferedTypeIds: [],
            filterOfferedRanks: [],
            filterWantedTypeIds: [],
            pendingStoneId: null,
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/item',
            position: 'journey/position',
        }),
        isInCity() {
            return !!this.position && this.position.type === Positions.CITY;
        },
        tabs() {
            return [
                {id: 'marketplace', label: this.$t('trade.hub.tabMarketplace')},
                {id: 'mine', label: this.$t('trade.hub.tabMine')},
            ];
        },
        transparentClass() {
            return this.$vuetify.theme.dark ? 'transparent03--dark' : 'transparent03';
        },
        myOpenOffers() {
            return this.myOffers.filter((o) => o.status === 'OPEN');
        },
        myUnlockedStoneOptions() {
            return this.myStones
                .filter((s) => !s.lockedInTradeOffer)
                .map((s) => ({id: s.id, label: `${s.name} (${s.stoneVal}, ${s.type.name})`}));
        },
        matchingStoneOptions() {
            if (!this.fulfillOfferTarget) {
                return [];
            }
            const wantedTypeIds = this.fulfillOfferTarget.wantedTypes.map((t) => t.id);
            return this.myStones
                .filter((s) => !s.lockedInTradeOffer
                    && wantedTypeIds.includes(s.type.id)
                    && this.isPowerRankAcceptable(s.stoneVal, this.fulfillOfferTarget))
                .map((s) => ({id: s.id, label: `${s.name} (${s.stoneVal}, ${s.type.name})`}));
        },
        selectedNewOfferStone() {
            return this.myStones.find((s) => s.id === this.newOfferItemId) || null;
        },
        isNewOfferStoneWeakest() {
            if (!this.selectedNewOfferStone) {
                return false;
            }
            return StonePowerRankOrder.indexOf(this.selectedNewOfferStone.stoneVal) === 0;
        },
        availableWantedTypesForNewOffer() {
            if (!this.selectedNewOfferStone) {
                return this.allTypes;
            }
            return this.allTypes.filter((t) => t.id !== this.selectedNewOfferStone.type.id);
        },
        availableOfferedTypesForFilter() {
            const seen = new Map();
            this.marketplaceOffers.forEach((offer) => {
                const type = offer.offeredItem?.type;
                if (type && !seen.has(type.id)) {
                    seen.set(type.id, type);
                }
            });
            const typeOrder = this.allTypes.map((t) => t.id);
            return Array.from(seen.values()).sort((a, b) => typeOrder.indexOf(a.id) - typeOrder.indexOf(b.id));
        },
        availableOfferedRanksForFilter() {
            // requiredPowerRank = snapshot sily NABIZENEHO kamene, ne samostatne pole pozadovane sily
            const seen = new Set();
            this.marketplaceOffers.forEach((offer) => {
                if (offer.requiredPowerRank) {
                    seen.add(offer.requiredPowerRank);
                }
            });
            return StonePowerRankOrder.filter((rank) => seen.has(rank));
        },
        availableWantedTypesForFilter() {
            const seen = new Map();
            this.marketplaceOffers.forEach((offer) => {
                offer.wantedTypes.forEach((type) => {
                    if (!seen.has(type.id)) {
                        seen.set(type.id, type);
                    }
                });
            });
            const typeOrder = this.allTypes.map((t) => t.id);
            return Array.from(seen.values()).sort((a, b) => typeOrder.indexOf(a.id) - typeOrder.indexOf(b.id));
        },
        activeFilterCount() {
            return this.filterOfferedTypeIds.length + this.filterOfferedRanks.length + this.filterWantedTypeIds.length;
        },
        filteredMarketplaceOffers() {
            return this.marketplaceOffers.filter((offer) => {
                if (this.filterOfferedTypeIds.length > 0
                    && !this.filterOfferedTypeIds.includes(offer.offeredItem?.type?.id)) {
                    return false;
                }
                if (this.filterOfferedRanks.length > 0
                    && !this.filterOfferedRanks.includes(offer.requiredPowerRank)) {
                    return false;
                }
                if (this.filterWantedTypeIds.length > 0
                    && !offer.wantedTypes.some((t) => this.filterWantedTypeIds.includes(t.id))) {
                    return false;
                }
                return true;
            });
        },
    },
    watch: {
        selectedNewOfferStone(stone) {
            if (stone) {
                this.newOfferWantedTypeIds = this.newOfferWantedTypeIds.filter((id) => id !== stone.type.id);
            }
            if (this.isNewOfferStoneWeakest) {
                this.newOfferAcceptWeakerStone = false;
            }
        },
    },
    created() {
        // prichod z Baťohu (Stones.vue "Nabidnout na Trhu") - kamen se predvybere az po nacteni myStones
        if (this.$route.query.stoneId) {
            this.activeTab = 'mine';
            this.pendingStoneId = Number(this.$route.query.stoneId);
        }
        this.loadMarketplace();
        this.loadMine();
        this.loadMyStones();
        this.loadAllStoneTypes();
    },
    methods: {
        typeChipStyle,
        ...mapActions({
            loadTradeMarketplaceAction: 'trainer/loadTradeMarketplace',
            loadMyTradeOffersAction: 'trainer/loadMyTradeOffers',
            loadStoneTypesAction: 'trainer/loadStoneTypes',
            createTradeOfferAction: 'trainer/createTradeOffer',
            fulfillTradeOfferAction: 'trainer/fulfillTradeOffer',
            cancelTradeOfferAction: 'trainer/cancelTradeOffer',
            fetchTrainerObject: 'trainer/fetchObject',
        }),
        loadMarketplace() {
            this.marketplaceLoading = true;
            this.loadTradeMarketplaceAction().then((data) => {
                this.marketplaceLoading = false;
                this.marketplaceOffers = data || [];
            });
        },
        loadMine() {
            this.loadMyTradeOffersAction().then((data) => {
                this.myOffers = data || [];
            });
        },
        loadMyStones() {
            if (!this.user || !this.user.id) {
                return;
            }
            const url = new URL('api/trainers/' + this.user.id, API_ENTRYPOINT);
            this.fetchTrainerObject({url: url, method: 'GET'}).then((data) => {
                if (!data || !data.items) {
                    return;
                }
                const items = Object.keys(data.items).map((key) => data.items[key]);
                this.myStones = items.filter((item) => item.category === Categories.CATEGORY_STONE);
                if (this.pendingStoneId !== null) {
                    const stone = this.myStones.find((s) => s.id === this.pendingStoneId && !s.lockedInTradeOffer);
                    if (stone) {
                        this.newOfferItemId = stone.id;
                    }
                    this.pendingStoneId = null;
                }
            });
        },
        loadAllStoneTypes() {
            this.loadStoneTypesAction().then((data) => {
                this.allTypes = data || [];
            });
        },
        wantedTypeNames(offer) {
            return offer.wantedTypes.map((t) => this.$t('common.typeNames.' + t.name)).join(', ');
        },
        canFulfill(offer) {
            const wantedTypeIds = offer.wantedTypes.map((t) => t.id);
            return this.myStones.some((s) => !s.lockedInTradeOffer
                && wantedTypeIds.includes(s.type.id)
                && this.isPowerRankAcceptable(s.stoneVal, offer));
        },
        isPowerRankAcceptable(myRank, offer) {
            const requiredIndex = StonePowerRankOrder.indexOf(offer.requiredPowerRank);
            const diff = StonePowerRankOrder.indexOf(myRank) - requiredIndex;
            return diff === 0 || diff === 1 || (diff === -1 && offer.acceptWeakerStone);
        },
        toggleWantedType(typeId) {
            const idx = this.newOfferWantedTypeIds.indexOf(typeId);
            if (idx === -1) {
                this.newOfferWantedTypeIds.push(typeId);
            } else {
                this.newOfferWantedTypeIds.splice(idx, 1);
            }
        },
        toggleInFilterList(list, id) {
            const idx = list.indexOf(id);
            if (idx === -1) {
                list.push(id);
            } else {
                list.splice(idx, 1);
            }
        },
        clearFilters() {
            this.filterOfferedTypeIds = [];
            this.filterOfferedRanks = [];
            this.filterWantedTypeIds = [];
        },
        submitCreateOffer() {
            this.createBusy = true;
            this.createError = '';
            this.createTradeOfferAction({
                itemId: this.newOfferItemId,
                wantedTypeIds: this.newOfferWantedTypeIds,
                acceptWeakerStone: this.newOfferAcceptWeakerStone,
            })
                .then(() => {
                    this.createBusy = false;
                    this.newOfferItemId = null;
                    this.newOfferWantedTypeIds = [];
                    this.newOfferAcceptWeakerStone = false;
                    this.loadMine();
                    this.loadMyStones();
                    this.$toast.success(this.$t('trade.hub.createSuccessToast'), {timeout: 3000, hideProgressBar: true});
                })
                .catch((e) => {
                    this.createBusy = false;
                    this.createError = e.message;
                });
        },
        openFulfillDialog(offer) {
            this.fulfillOfferTarget = offer;
            this.fulfillMyItemId = null;
            this.fulfillError = '';
            this.fulfillDialogOpen = true;
        },
        submitFulfill() {
            this.fulfillBusy = true;
            this.fulfillError = '';
            this.fulfillTradeOfferAction({tradeOfferId: this.fulfillOfferTarget.id, myItemId: this.fulfillMyItemId})
                .then(() => {
                    this.fulfillBusy = false;
                    this.fulfillDialogOpen = false;
                    this.loadMarketplace();
                    this.loadMyStones();
                    this.$toast.success(this.$t('trade.hub.fulfillSuccessToast'), {timeout: 3000, hideProgressBar: true});
                })
                .catch((e) => {
                    this.fulfillBusy = false;
                    this.fulfillError = e.message;
                });
        },
        async cancelOffer(offer) {
            const rlyCancel = await this.$refs.confirmDialog.open(this.$t('trade.hub.confirmCancel'));
            if (!rlyCancel) {
                return;
            }
            this.cancelTradeOfferAction(offer.id)
                .then(() => {
                    this.loadMine();
                    this.loadMyStones();
                })
                .catch((e) => {
                    this.$toast.error(e.message, {timeout: 4000, hideProgressBar: true});
                });
        },
        statusLabel(offer) {
            if (offer.status === 'OPEN') {
                return this.$t('trade.hub.statusOpen');
            }
            if (offer.status === 'CANCELLED') {
                return this.$t('trade.hub.statusCancelled');
            }
            return this.$t('trade.hub.statusCompleted', {
                name: offer.buyer ? offer.buyer.name : '',
                received: offer.receivedItem ? offer.receivedItem.name : '',
            });
        },
    },
};
</script>

<style scoped>
.trade-hub-page {
    height: 100%;
    display: flex;
    flex-direction: column;
}
.trade-hub-page__scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: 8px;
    padding-bottom: 24px;
}
.trade-hub-title-pill-wrap {
    display: flex;
    justify-content: center;
    padding: 12px 0 8px;
}
.trade-hub-title-pill {
    display: inline-block;
    background: rgba(0, 0, 0, .65);
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    padding: 6px 20px;
    border-radius: 20px;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, .6);
}
.trade-hub-city-notice {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0 8px 8px;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, .5);
}
.trade-hub-tabs {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 0 8px 12px;
}
.trade-hub-tab-pill {
    background: rgba(0, 0, 0, .35);
    color: #fff;
    border: none;
    border-radius: 16px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
}
.trade-hub-tab-pill--active {
    background: #FFD700;
    color: #000;
}
.trade-hub-section {
    padding: 4px 8px 16px;
}
.trade-hub-section__title {
    font-size: 16px;
    font-weight: 700;
    padding: 4px 4px 8px;
}
.trade-hub-type-chips {
    display: flex;
    flex-wrap: wrap;
}
::v-deep .trade-hub-type-chip--active {
    border: 2px solid #FB8C00 !important;
}
.trade-hub-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    cursor: pointer;
    margin-bottom: 4px;
}
.trade-hub-row--static {
    cursor: default;
}
.trade-hub-row__stone-img {
    flex: 0 0 auto;
    width: 40px;
    height: 48px;
}
.trade-hub-row__body {
    flex: 1 1 auto;
    min-width: 0;
}
.trade-hub-row__name {
    font-weight: 700;
    font-size: 14px;
}
.trade-hub-row__preview {
    font-size: 12px;
    opacity: .75;
}
.trade-hub-row__seller {
    font-size: 11px;
    opacity: .65;
}
.trade-hub-row__mine-badge {
    display: inline-block;
    margin-top: 2px;
    font-size: 10px;
    font-weight: 700;
    color: #000;
    background: #FFD700;
    border-radius: 8px;
    padding: 1px 8px;
}
.trade-hub-row--unavailable {
    opacity: .5;
}
.trade-hub-row--unavailable .trade-hub-row__stone-img {
    filter: grayscale(1);
}
.trade-hub-empty {
    font-size: 13px;
}
.trade-hub-weakest-hint {
    font-size: 11px;
    opacity: .65;
}
.trade-hub-hint {
    text-align: center;
    font-size: 11px;
    opacity: .75;
    padding: 0 8px 8px;
}
/* !important + zretezena .pa-4 nutne - samotna .trade-hub-fulfill-body ma stejnou specificitu jako Vuetify ".v-application .pa-4" a prohrava podle poradi nacteni CSS bundlu */
.pa-4.trade-hub-fulfill-body {
    padding-bottom: var(--responsive-dialog-close-clearance) !important;
}
.trade-hub-filters {
    padding: 0 4px 8px;
}
.trade-hub-filters-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(0, 0, 0, .35);
    color: #fff;
    border: none;
    border-radius: 14px;
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}
.trade-hub-filters-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background: #FFD700;
    color: #000;
    font-size: 10px;
    font-weight: 700;
}
.trade-hub-filters-panel {
    margin-top: 8px;
    padding: 8px;
    border-radius: 10px;
    background: rgba(0, 0, 0, .2);
}
.trade-hub-filters-group-label {
    font-size: 11px;
    font-weight: 700;
    opacity: .75;
    padding: 2px 4px;
}
.trade-hub-row__weaker-badge {
    display: inline-block;
    margin-top: 2px;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    background: rgba(0, 0, 0, .5);
    border-radius: 8px;
    padding: 1px 8px;
}
</style>
